import { useMemo } from "react";
import { isVideoSeason, isVideoLegacySeason, isVideoSpotLiveSeason } from "~/components/blocks/product/type-guard";
import { usePurchaseModalActions } from "~/hooks/use-purchase-modal-actions";

import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { G_VideoContentType, G_VideoPpvExpirationType } from "~/lib/graphql";
import { formatDateToYMDHm } from "~/util/format-date";

export type UseViewConditionTagArgs = {
  videoData: G_GetVideoQuery["video"];
};

export const useViewConditionTag = (args: UseViewConditionTagArgs) => {
  const { videoData } = args;

  const { content } = useQueryString();
  const { pushWithQueriesPurchaseModal } = usePurchaseModalActions();

  /** 無料タグの表示ロジックの判定式に使用する */
  const freeProduct = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;

      const {
        episode: { freeProduct },
      } = videoData;

      return freeProduct;
    }

    // エピソード末端でない複数話 もしくは 単数話
    if (isVideoSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;

      return videoData.continueWatching ? videoData.continueWatching.content.freeProduct : edges[0].node.freeProduct;
    }

    // スポット配信の場合
    if (isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.freeProduct;
    }

    // グラビアの場合
    if (isVideoLegacySeason(videoData)) {
      const {
        content: { freeProduct },
      } = videoData;

      return freeProduct;
    }

    return;
  }, [videoData]);

  /** PPVタグの表示ロジックの判定式に使用する */
  const priceSummary = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;

      const {
        episode: { priceSummary },
      } = videoData;

      return priceSummary;
    }

    // エピソード末端でない複数話 もしくは 単数話 もしくは スポット配信の場合
    if (isVideoSeason(videoData) || isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.priceSummary;
    }

    // グラビアの場合
    if (isVideoLegacySeason(videoData)) {
      const {
        content: { priceSummary },
      } = videoData;

      return priceSummary;
    }

    return;
  }, [videoData]);

  /** プレミアムタグの表示ロジックの判定式に使用する */
  const svodProduct = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;

      const {
        episode: { svodProduct },
      } = videoData;

      return svodProduct;
    }

    // エピソード末端でない複数話 もしくは 単数話 もしくは スポット配信の場合
    if (isVideoSeason(videoData) || isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.svodProduct;
    }

    // グラビアの場合
    if (isVideoLegacySeason(videoData)) {
      const {
        content: { svodProduct },
      } = videoData;

      return svodProduct;
    }

    return;
  }, [videoData]);

  const ppvExpiration = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;

      const {
        episode: { ppvExpiration },
      } = videoData;

      return ppvExpiration;
    }

    // エピソード末端でない複数話 もしくは 単数話 もしくは スポット配信の場合
    if (isVideoSeason(videoData) || isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.ppvExpiration;
    }

    // グラビアの場合
    if (isVideoLegacySeason(videoData)) {
      const { content } = videoData;
      return content.ppvExpiration;
    }

    return;
  }, [videoData]);

  const purchaseSubText = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return "";

      const {
        episode: { ppvExpiration },
      } = videoData;

      if (ppvExpiration === null || ppvExpiration === undefined) return "";

      if (ppvExpiration.expirationType === G_VideoPpvExpirationType.Est) {
        return "視聴期限：無期限";
      }

      if (ppvExpiration.expirationType === G_VideoPpvExpirationType.TvodDynamicExpiration) {
        if (!ppvExpiration.viewingExpiration) {
          if (!ppvExpiration.viewingStartExpiration) return "";
          return `${formatDateToYMDHm(ppvExpiration.viewingStartExpiration)}までに視聴開始してください`;
        } else {
          return `視聴期限：${formatDateToYMDHm(ppvExpiration.viewingExpiration)}`;
        }
      }

      if (ppvExpiration?.expirationType === G_VideoPpvExpirationType.TvodFixedExpiration) {
        if (!ppvExpiration.viewingExpiration) return "";

        if (ppvExpiration.startDeliveryAt) {
          const { viewingExpiration, startDeliveryAt } = ppvExpiration;

          /** クライアントの時刻が 配信開始日より前 の場合 */
          const isBeforeDeliveryDate = new Date(startDeliveryAt).getTime() > Date.now();

          if (isBeforeDeliveryDate) {
            return `視聴期限：${formatDateToYMDHm(startDeliveryAt)} - ${formatDateToYMDHm(viewingExpiration)}`;
          } else {
            // クライアントの時刻が 配信開始日より後 の場合
            return `視聴期限：${formatDateToYMDHm(viewingExpiration)}`;
          }
        }
      }
    }

    // エピソード末端でない複数話 もしくは 単数話 もしくは スポット配信の場合
    if (isVideoSeason(videoData) || isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return "";

      if (edges[0].node.ppvExpiration?.expirationType === G_VideoPpvExpirationType.Est) {
        return "視聴期限：無期限";
      }

      if (edges[0].node.ppvExpiration?.expirationType === G_VideoPpvExpirationType.TvodDynamicExpiration) {
        if (!edges[0].node.ppvExpiration.viewingExpiration) {
          if (!edges[0].node.ppvExpiration.viewingStartExpiration) return "";
          return `${formatDateToYMDHm(edges[0].node.ppvExpiration.viewingStartExpiration)}までに視聴開始してください`;
        } else {
          return `視聴期限：${formatDateToYMDHm(edges[0].node.ppvExpiration.viewingExpiration)}`;
        }
      }

      if (edges[0].node.ppvExpiration?.expirationType === G_VideoPpvExpirationType.TvodFixedExpiration) {
        if (!edges[0].node.ppvExpiration.viewingExpiration) return "";

        if (edges[0].node.ppvExpiration.startDeliveryAt) {
          const { viewingExpiration, startDeliveryAt } = edges[0].node.ppvExpiration;

          /** クライアントの時刻が 配信開始日より前 の場合 */
          const isBeforeDeliveryDate = new Date(startDeliveryAt).getTime() > Date.now();

          if (isBeforeDeliveryDate) {
            return `視聴期限：${formatDateToYMDHm(startDeliveryAt)} - ${formatDateToYMDHm(viewingExpiration)}`;
          } else {
            // クライアントの時刻が 配信開始日より後 の場合
            return `視聴期限：${formatDateToYMDHm(viewingExpiration)}`;
          }
        }
      }
    }

    // グラビア作品の場合
    if (isVideoLegacySeason(videoData)) {
      const { content } = videoData;

      if (content.ppvExpiration?.expirationType === G_VideoPpvExpirationType.Est) {
        return "視聴期限：無期限";
      }

      if (content.ppvExpiration?.expirationType === G_VideoPpvExpirationType.TvodDynamicExpiration) {
        if (!content.ppvExpiration.viewingExpiration) {
          if (!content.ppvExpiration.viewingStartExpiration) return "";
          return `${formatDateToYMDHm(content.ppvExpiration.viewingStartExpiration)}までに視聴開始してください`;
        } else {
          return `視聴期限：${formatDateToYMDHm(content.ppvExpiration.viewingExpiration)}`;
        }
      }

      if (content.ppvExpiration?.expirationType === G_VideoPpvExpirationType.TvodFixedExpiration) {
        if (!content.ppvExpiration.viewingExpiration) return "";

        if (content.ppvExpiration.startDeliveryAt) {
          const { viewingExpiration, startDeliveryAt } = content.ppvExpiration;

          /** クライアントの時刻が 配信開始日より前 の場合 */
          const isBeforeDeliveryDate = new Date(startDeliveryAt).getTime() > Date.now();

          // クライアントの時刻が 配信開始日より前 の場合
          if (isBeforeDeliveryDate) {
            return `視聴期限：${formatDateToYMDHm(startDeliveryAt)} - ${formatDateToYMDHm(viewingExpiration)}`;
          } else {
            // クライアントの時刻が 配信開始日より後 の場合
            return `視聴期限：${formatDateToYMDHm(viewingExpiration)}`;
          }
        }
      }
    }

    return "";
  }, [videoData]);

  /** その他の購入方法を表示するための条件式 */
  const isAnotherPurchased = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return false;

      const { episode } = videoData;

      if (episode.viewingRights.isStreamable) {
        const isAllPurchased = episode.ppvProducts.every((cur) => cur.isPurchased);

        if (episode.ppvProducts.length === 0) return false;
        if (episode.ppvProducts.length === 1 && isAllPurchased) return false;
        if (episode.ppvProducts.length > 2 && isAllPurchased) return false;

        return true;
      }
    }

    // エピソード末端でない複数話 もしくは 単数話 もしくは スポット配信の場合
    if (isVideoSeason(videoData) || isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return false;

      if (edges[0].node.viewingRights.isStreamable) {
        const isAllPurchased = edges[0].node.ppvProducts.every((cur) => cur.isPurchased);

        if (edges[0].node.ppvProducts.length === 0) return false;
        if (edges[0].node.ppvProducts.length === 1 && isAllPurchased) return false;
        if (edges[0].node.ppvProducts.length > 2 && isAllPurchased) return false;

        return true;
      }
    }

    // グラビアの場合
    if (isVideoLegacySeason(videoData)) {
      const { content } = videoData;

      if (content.contentType === G_VideoContentType.VodVr) {
        const isAllPurchased = content.ppvProducts.every((cur) => cur.isPurchased);

        if (content.ppvProducts.length === 0) return false;
        if (content.ppvProducts.length === 1 && isAllPurchased) return false;
        if (content.ppvProducts.length > 1 && isAllPurchased) return false;

        return true;
      }

      if (content.contentType === G_VideoContentType.Vod_2D) {
        if (content.viewingRights.isStreamable) {
          const isAllPurchased = content.ppvProducts.every((cur) => cur.isPurchased);

          if (content.ppvProducts.length === 0) return false;
          if (content.ppvProducts.length === 1 && isAllPurchased) return false;
          if (content.ppvProducts.length > 1 && isAllPurchased) return false;

          return true;
        }
      }
    }

    return false;
  }, [videoData]);

  /** その他の購入方法ボタン押下時に発火するイベントハンドラ */
  const anotherPurchaseBtnClicked = () => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;

      const { episode } = videoData;

      pushWithQueriesPurchaseModal(episode.id);

      return;
    }

    // エピソード末端でない複数話 もしくは 単数話 もしくは スポット配信の場合
    if (isVideoSeason(videoData) || isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;

      pushWithQueriesPurchaseModal(edges[0].node.id);

      return;
    }

    // グラビアの場合
    if (isVideoLegacySeason(videoData)) {
      const { content } = videoData;

      pushWithQueriesPurchaseModal(content.id);
    }
  };

  return {
    freeProduct,
    priceSummary,
    svodProduct,
    ppvExpiration,
    purchaseSubText,
    isAnotherPurchased,
    anotherPurchaseBtnClicked,
  };
};
